import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
} from "@mui/material";
import { Email, Phone, LocationOn } from "@mui/icons-material";
import { formatPhoneNumber } from "../../../../../helper/phoneFormatter.helper";

type CustomerShape = {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  business?: { name?: string };
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  isActive?: boolean;
};

type Props = {
  customer: CustomerShape;
};

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
      <Box
        sx={{
          width: { xs: 40, sm: 44 },
          height: { xs: 40, sm: 44 },
          borderRadius: "50%",
          background: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
          mt: "2px",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0, textAlign: "left" }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "0.78rem", sm: "0.85rem" },
            color: "text.primary",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.95rem", sm: "1rem" },
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const CustomerFullDetailsCard: React.FC<Props> = ({ customer }) => {
  const name =
    `${customer.firstName || ""} ${customer.middleName || ""} ${customer.lastName || ""}`.trim();
  const displayBusiness = customer.business?.name || "Independent Customer";

  return (
    <Card
      sx={{
        mb: { xs: 2, sm: 3 },
        borderRadius: { xs: 2.5, sm: 3 },
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 1,
        background: "background.paper",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header row: name + chips */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1.5, md: 2 },
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "1.2rem", sm: "1.8rem", md: "1.5rem" },
                lineHeight: 1.05,
                wordBreak: "break-word",
              }}
            >
              {name || "Customer"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    color: "text.primary",
                  }}
                >
                  Business Name: {displayBusiness}
                </Typography>
              </Box>

              {(() => {
                const isActive = customer.isActive === true;
                return (
                  <Chip
                    label={isActive ? "Active" : "Inactive"}
                    color={isActive ? "success" : "error"}
                    sx={{
                      px: 2,
                      py: 1.1,
                      borderRadius: 999,
                      fontWeight: 800,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                      border: "1px solid",
                      ".MuiChip-label": {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      },
                    }}
                  />
                );
              })()}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 2, md: 2.5 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.15fr 1fr" },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {/* Contact */}
          <Box
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: "grey.50",
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                mb: 1.5,
                fontSize: { xs: "1.15rem", sm: "1.25rem" },
              }}
            >
              Contact Information
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
              <InfoRow
                icon={<Email sx={{ color: "#1976d2", fontSize: 22 }} />}
                label="Email Address"
                value={customer.email || "N/A"}
              />

              <Box
                sx={{
                  "& .MuiBox-root": {
                    // no-op; keep deterministic
                  },
                }}
              />

              <InfoRow
                icon={<Phone sx={{ color: "#1976d2", fontSize: 22 }} />}
                label="Phone Number"
                value={formatPhoneNumber(customer.phone || "") || "N/A"}
              />
            </Box>
          </Box>

          {/* Address */}
          <Box
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: "grey.50",
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                mb: 1.5,
                fontSize: { xs: "1.15rem", sm: "1.25rem" },
              }}
            >
              Address Information
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
              <InfoRow
                icon={<LocationOn sx={{ color: "#1976d2", fontSize: 22 }} />}
                label="Street Address"
                value={customer.street || "N/A"}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 1.2,
                }}
              >
                <Box>
                  <InfoRow
                    icon={<LocationOn sx={{ color: "#1976d2", fontSize: 20 }} />}
                    label="City"
                    value={customer.city || "N/A"}
                  />
                </Box>
                <Box>
                  <InfoRow
                    icon={<LocationOn sx={{ color: "#1976d2", fontSize: 20 }} />}
                    label="State"
                    value={customer.state || "N/A"}
                  />
                </Box>
                <Box>
                  <InfoRow
                    icon={<LocationOn sx={{ color: "#1976d2", fontSize: 20 }} />}
                    label="Zipcode"
                    value={customer.zipcode || "N/A"}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerFullDetailsCard;
